import { Box, Container, Divider, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import Footer from "../components/common/Footer";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      {children}
    </Box>
  );
}

export default function TermsPage() {
  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "background.default" }}>
      <Container maxWidth="md" sx={{ flex: 1, py: 6 }}>
        <Link component={RouterLink} to="/" variant="body2" color="text.secondary" underline="hover" sx={{ display: "inline-block", mb: 4 }}>
          ← Back to īmēra
        </Link>

        <Typography variant="h4" gutterBottom>Terms of Service</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last updated: May 2026
        </Typography>

        <Divider sx={{ mb: 4 }} />

        <Section title="Acceptance of Terms">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            By accessing or using īmēra (the "Service"), operated by TL Lab, you agree to be bound by these Terms of Service. If you do not agree, please do not use the Service.
          </Typography>
        </Section>

        <Section title="Use of Service">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            You agree to use īmēra only for lawful purposes and in accordance with these Terms. You must not:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2.5, "& li": { mb: 0.5 } }}>
            <Typography component="li" variant="body2" color="text.secondary">Send unsolicited bulk email, spam, or content that violates anti-spam laws.</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Send content that is unlawful, harmful, abusive, deceptive, or infringes third-party rights.</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Attempt to gain unauthorised access to the Service or its infrastructure.</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Interfere with or disrupt the integrity or performance of the Service or upstream email providers.</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Use the Service in any way that violates applicable laws or regulations.</Typography>
          </Box>
        </Section>

        <Section title="Accounts and API Keys">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            Administrators are responsible for maintaining the confidentiality of their account credentials and any API keys issued to consuming apps. You are responsible for all activity that occurs under your account or your API keys. Notify us immediately at{" "}
            <Link href="mailto:support@techlead.co.nz" color="primary">support@techlead.co.nz</Link>{" "}
            if you suspect any unauthorised use, and revoke the affected key from the dashboard.
          </Typography>
        </Section>

        <Section title="Verified Domains">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            īmēra only sends from domains that have been verified with an upstream provider and registered in the dashboard. You are responsible for ensuring you have authority to send from any domain you add and for maintaining its DNS records (SPF, DKIM, DMARC).
          </Typography>
        </Section>

        <Section title="Content">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            You retain ownership of email content you submit through īmēra — recipients, subjects, bodies, templates, and template variables. By submitting content to the Service, you grant TL Lab a limited licence to process, transmit, and store that content solely for the purpose of providing the Service to you.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, lineHeight: 1.8 }}>
            You are solely responsible for the content you send and for compliance with applicable laws (including privacy and anti-spam laws) in the jurisdictions of your recipients.
          </Typography>
        </Section>

        <Section title="Termination">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            We reserve the right to suspend or terminate access to the Service at our discretion, including for violations of these Terms or upstream provider terms. You may stop using the Service at any time by revoking your API keys.
          </Typography>
        </Section>

        <Section title="Disclaimers">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. TL Lab does not warrant that the Service will be uninterrupted, error-free, or that delivery will succeed for any given message. To the fullest extent permitted by law, TL Lab disclaims all liability for any loss or damage arising from your use of the Service.
          </Typography>
        </Section>

        <Section title="Governing Law">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            These Terms are governed by the laws of New Zealand. Any disputes shall be subject to the exclusive jurisdiction of the courts of New Zealand.
          </Typography>
        </Section>

        <Section title="Changes to These Terms">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            We may update these Terms from time to time. Continued use of the Service after changes are posted constitutes your acceptance of the revised Terms.
          </Typography>
        </Section>

        <Section title="Contact Us">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            For questions about these Terms, contact us at{" "}
            <Link href="mailto:support@techlead.co.nz" color="primary">support@techlead.co.nz</Link>.
          </Typography>
        </Section>
      </Container>

      <Footer />
    </Box>
  );
}
